/**
 * FinancialSummaryPanel
 *
 * Displays aggregated financial KPIs, deal pipeline breakdown, funded loan totals,
 * and a sortable entity breakdown table for a client group.
 *
 * Uses GET /api/crm/client-groups/:id/financial-summary (F005 endpoint).
 *
 * @sprint SPRINT-26
 * @feature F010
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import {
  Building2,
  TrendingUp,
  BarChart2,
  Banknote,
  DollarSign,
  AlertCircle,
  ArrowUpDown,
  RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StageBreakdown {
  count: number;
  value: string;
}

interface FinancialSummaryResponse {
  clientGroupId: string;
  clientGroupName: string;
  summary: {
    entityCount: number;
    contactCount: number;
    totalAnnualRevenue: string;
    totalEmployeeCount: number;
  };
  dealPipeline: {
    activeDealCount: number;
    totalPipelineValue: string;
    byStage: {
      scenario: StageBreakdown;
      deal: StageBreakdown;
      funded_loan: StageBreakdown;
    };
  };
  fundedLoans: {
    totalFundedCount: number;
    totalFundedValue: string;
    totalCurrentBalance: string;
    averageRate: string | null;
  };
  entities: Array<{
    accountId: string;
    legalName: string;
    entityType: string | null;
    annualRevenue: string;
    activeDealCount: number;
    fundedLoanCount: number;
  }>;
}

interface PipelineDeal {
  id: string;
  dealName: string;
  amountRequested: string | null;
  transactionStage: string;
}

// ---------------------------------------------------------------------------
// Formatting utilities
// ---------------------------------------------------------------------------

const formatCurrency = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return "$0";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "$0";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

const formatRate = (value: string | null): string => {
  if (!value) return "—";
  const num = parseFloat(value);
  if (isNaN(num)) return "—";
  return `${(num * 100).toFixed(2)}%`;
};

const formatEntityType = (type: string | null): string => {
  if (!type) return "—";
  return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, " ");
};

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function KpiSkeleton() {
  return (
    <Card className="animate-pulse">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <div className="h-3 w-24 rounded bg-muted" />
        <div className="h-4 w-4 rounded bg-muted" />
      </CardHeader>
      <CardContent>
        <div className="h-8 w-32 rounded bg-muted mb-1" />
        <div className="h-3 w-20 rounded bg-muted" />
      </CardContent>
    </Card>
  );
}

function TableSkeleton() {
  return (
    <div className="animate-pulse space-y-2 p-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-8 w-full rounded bg-muted" />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage colours for pipeline bar
// ---------------------------------------------------------------------------

const STAGE_COLORS: Record<string, string> = {
  scenario: "bg-amber-400",
  deal: "bg-blue-500",
  funded_loan: "bg-green-500",
};

const STAGE_LABELS: Record<string, string> = {
  scenario: "Scenario",
  deal: "Deal",
  funded_loan: "Funded Loan",
};

// ---------------------------------------------------------------------------
// Sort types
// ---------------------------------------------------------------------------

type EntitySortField = "legalName" | "annualRevenue" | "activeDealCount" | "fundedLoanCount";
type SortDir = "asc" | "desc";

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface FinancialSummaryPanelProps {
  clientGroupId: string;
}

export function FinancialSummaryPanel({ clientGroupId }: FinancialSummaryPanelProps) {
  const [sortField, setSortField] = useState<EntitySortField>("legalName");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const { data, isLoading, isError, refetch } = useQuery<FinancialSummaryResponse>({
    queryKey: [`/api/crm/client-groups/${clientGroupId}/financial-summary`],
    enabled: !!clientGroupId,
  });
  const { data: deals = [] } = useQuery<PipelineDeal[]>({
    queryKey: ["/api/crm/deals", { clientGroupId }],
    enabled: !!clientGroupId,
  });

  const toggleSort = (field: EntitySortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  // ------------------------------------------------------------------
  // Loading state
  // ------------------------------------------------------------------

  if (isLoading) {
    return (
      <div data-testid="panel-financial-summary" className="space-y-4">
        <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
          <KpiSkeleton />
          <KpiSkeleton />
          <KpiSkeleton />
          <KpiSkeleton />
        </div>
        <Card>
          <CardHeader>
            <div className="h-4 w-24 rounded bg-muted animate-pulse" />
          </CardHeader>
          <CardContent>
            <TableSkeleton />
          </CardContent>
        </Card>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Error state
  // ------------------------------------------------------------------

  if (isError) {
    return (
      <div data-testid="panel-financial-summary">
        <Card className="border-destructive">
          <CardContent className="flex flex-col items-center justify-center py-8 gap-3">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p className="text-sm font-medium text-destructive">
              Unable to load financial summary
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              data-testid="button-retry-financial-summary"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const { summary, dealPipeline, fundedLoans, entities } = data;

  // ------------------------------------------------------------------
  // Empty group state (no accounts)
  // ------------------------------------------------------------------

  if (summary.entityCount === 0) {
    return (
      <div data-testid="panel-financial-summary">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
            <Building2 className="h-10 w-10 text-muted-foreground" />
            <p className="text-sm font-medium">No entities in this group</p>
            <p className="text-xs text-muted-foreground text-center">
              Add accounts to this client group to see financial summary
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Revenue colour
  // ------------------------------------------------------------------

  const revenueNum = parseFloat(summary.totalAnnualRevenue);
  const revenueValueClass =
    revenueNum > 0 ? "text-2xl font-semibold text-green-600" : "text-2xl font-semibold text-muted-foreground";

  // ------------------------------------------------------------------
  // Pipeline stacked bar
  // ------------------------------------------------------------------

  const totalPipelineNum = parseFloat(dealPipeline.totalPipelineValue);
  const stages: Array<"scenario" | "deal" | "funded_loan"> = ["scenario", "deal", "funded_loan"];
  const stageDeals = deals.filter((deal) => stages.includes(deal.transactionStage as typeof stages[number]));

  // ------------------------------------------------------------------
  // Sorted entity table
  // ------------------------------------------------------------------

  const sortedEntities = [...entities].sort((a, b) => {
    let aVal: string | number;
    let bVal: string | number;

    if (sortField === "legalName") {
      aVal = a.legalName.toLowerCase();
      bVal = b.legalName.toLowerCase();
    } else if (sortField === "annualRevenue") {
      aVal = parseFloat(a.annualRevenue);
      bVal = parseFloat(b.annualRevenue);
    } else if (sortField === "activeDealCount") {
      aVal = a.activeDealCount;
      bVal = b.activeDealCount;
    } else {
      aVal = a.fundedLoanCount;
      bVal = b.fundedLoanCount;
    }

    if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <div data-testid="panel-financial-summary" className="space-y-4">
      {/* ------------------------------------------------------------------ */}
      {/* KPI Cards                                                           */}
      {/* ------------------------------------------------------------------ */}

      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        {/* Entity Count */}
        <Card data-testid="kpi-entity-count">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Entities
            </CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{summary.entityCount}</div>
            <p className="text-xs text-muted-foreground">Accounts in group</p>
          </CardContent>
        </Card>

        {/* Total Revenue */}
        <Card data-testid="kpi-total-revenue">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Annual Revenue
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={revenueValueClass}>
              {formatCurrency(summary.totalAnnualRevenue)}
            </div>
            <p className="text-xs text-muted-foreground">Total across entities</p>
          </CardContent>
        </Card>

        {/* Pipeline Value */}
        <Card data-testid="kpi-pipeline-value">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pipeline Value
            </CardTitle>
            <BarChart2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-blue-600">
              {formatCurrency(dealPipeline.totalPipelineValue)}
            </div>
            <p className="text-xs text-muted-foreground">
              {dealPipeline.activeDealCount} active deal{dealPipeline.activeDealCount !== 1 ? "s" : ""}
            </p>
          </CardContent>
        </Card>

        {/* Funded Loans */}
        <Card data-testid="kpi-funded-loans">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Funded Loans
            </CardTitle>
            <Banknote className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-green-600">
              {formatCurrency(fundedLoans.totalFundedValue)}
            </div>
            <p className="text-xs text-muted-foreground">
              {fundedLoans.totalFundedCount} settled facilit{fundedLoans.totalFundedCount !== 1 ? "ies" : "y"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Deal Pipeline Section                                              */}
      {/* ------------------------------------------------------------------ */}

      <Card>
        <CardHeader className="flex flex-row items-center gap-2 pb-3">
          <BarChart2 className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base font-medium">Deal Pipeline</CardTitle>
        </CardHeader>
        <CardContent data-testid="section-deal-pipeline" className="space-y-4">
          {dealPipeline.activeDealCount === 0 ? (
            <p className="text-sm text-muted-foreground">No active deals</p>
          ) : (
            <>
              {/* Stacked bar */}
              {totalPipelineNum > 0 && (
                <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
                  {stages.map((stage) => {
                    const stageVal = parseFloat(dealPipeline.byStage[stage]?.value ?? "0");
                    if (stageVal === 0) return null;
                    const width = ((stageVal / totalPipelineNum) * 100).toFixed(1);
                    return (
                      <div
                        key={stage}
                        className={`h-full ${STAGE_COLORS[stage]}`}
                        style={{ width: `${width}%` }}
                        title={`${STAGE_LABELS[stage]}: ${formatCurrency(stageVal)}`}
                      />
                    );
                  })}
                </div>
              )}

              {/* Stage rows */}
              <div className="space-y-2">
                {stages.map((stage) => {
                  const stageData = dealPipeline.byStage[stage];
                  if (!stageData || stageData.count === 0) return null;
                  return (
                    <div
                      key={stage}
                      data-testid={`pipeline-row-${stage}`}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`h-2.5 w-2.5 rounded-full ${STAGE_COLORS[stage]}`}
                        />
                        <span className="text-sm">{STAGE_LABELS[stage]}</span>
                        <Badge variant="secondary" className="text-xs px-1.5 py-0">
                          {stageData.count}
                        </Badge>
                      </div>
                      <span className="text-sm font-medium">
                        {formatCurrency(stageData.value)}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Deal cards (click-through to detail page) */}
              {stageDeals.length > 0 && (
                <div className="space-y-2 pt-1" data-testid="pipeline-deal-cards">
                  {stageDeals.slice(0, 5).map((deal) => (
                    <Link
                      key={deal.id}
                      href={`/deals/${deal.id}`}
                      data-testid={`pipeline-deal-card-${deal.id}`}
                      className="flex items-center justify-between rounded-md border p-2.5 transition-colors hover:bg-accent"
                    >
                      <div className="flex items-center gap-2">
                        <div className="rounded-full bg-primary/10 p-1.5">
                          <DollarSign className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{deal.dealName || "Untitled Deal"}</p>
                          <p className="text-xs text-muted-foreground">{STAGE_LABELS[deal.transactionStage] ?? deal.transactionStage}</p>
                        </div>
                      </div>
                      <span className="text-sm font-medium">
                        {formatCurrency(deal.amountRequested)}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Funded Loans Section                                               */}
      {/* ------------------------------------------------------------------ */}

      {fundedLoans.totalFundedCount > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center gap-2 pb-3">
            <Banknote className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base font-medium">Funded Loans</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              <div>
                <p className="text-xs text-muted-foreground">Total Funded</p>
                <p className="text-sm font-semibold">
                  {formatCurrency(fundedLoans.totalFundedValue)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {fundedLoans.totalFundedCount} facilit{fundedLoans.totalFundedCount !== 1 ? "ies" : "y"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Current Balance</p>
                <p className="text-sm font-semibold">
                  {formatCurrency(fundedLoans.totalCurrentBalance)}
                </p>
              </div>
              {fundedLoans.averageRate && (
                <div>
                  <p className="text-xs text-muted-foreground">Average Rate</p>
                  <p className="text-sm font-semibold">
                    {formatRate(fundedLoans.averageRate)}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Entity Breakdown Table                                             */}
      {/* ------------------------------------------------------------------ */}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">Entity Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table data-testid="table-entity-breakdown">
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 font-medium hover:bg-transparent"
                    onClick={() => toggleSort("legalName")}
                  >
                    Entity Name
                    <ArrowUpDown className="ml-1 h-3 w-3" />
                  </Button>
                </TableHead>
                <TableHead>Type</TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 font-medium hover:bg-transparent"
                    onClick={() => toggleSort("annualRevenue")}
                  >
                    Annual Revenue
                    <ArrowUpDown className="ml-1 h-3 w-3" />
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 font-medium hover:bg-transparent"
                    onClick={() => toggleSort("activeDealCount")}
                  >
                    Active Deals
                    <ArrowUpDown className="ml-1 h-3 w-3" />
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 font-medium hover:bg-transparent"
                    onClick={() => toggleSort("fundedLoanCount")}
                  >
                    Funded Loans
                    <ArrowUpDown className="ml-1 h-3 w-3" />
                  </Button>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedEntities.map((entity) => (
                <TableRow key={entity.accountId}>
                  <TableCell className="font-medium">
                    <Link
                      href={`/accounts/${entity.accountId}`}
                      className="text-primary hover:underline"
                    >
                      {entity.legalName}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatEntityType(entity.entityType)}
                  </TableCell>
                  <TableCell>
                    {parseFloat(entity.annualRevenue) > 0 ? (
                      <span className="text-green-600">
                        {formatCurrency(entity.annualRevenue)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">$0</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span
                      className={
                        entity.activeDealCount > 0 ? "font-medium" : "text-muted-foreground"
                      }
                    >
                      {entity.activeDealCount}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      className={
                        entity.fundedLoanCount > 0
                          ? "font-medium text-green-600"
                          : "text-muted-foreground"
                      }
                    >
                      {entity.fundedLoanCount}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
